import React, { startTransition } from "react";
import {
  Autocomplete,
  AppBar,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  IconButton,
  Link,
  TextField,
  Toolbar,
  Typography,
  createFilterOptions,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import UploadIcon from "@mui/icons-material/Upload";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import { useUpload } from "../features/globalUpload/GlobalUploadProvider";
import { SearchItem, usePageChromeState } from "./PageChromeContext";
import { useProjects } from "./ProjectsContext";

interface TopBarProps {
  showMenuButton: boolean;
  onMenuClick: () => void;
}

const filterSearchOptions = createFilterOptions<SearchItem>({
  stringify: (o) => `${o.label} ${o.secondary ?? ""}`,
  limit: 20,
});

const isMac =
  typeof navigator !== "undefined" && navigator.platform.includes("Mac");

const TopBar: React.FC<TopBarProps> = ({ showMenuButton, onMenuClick }) => {
  const navigate = useNavigate();
  const { openUpload } = useUpload();
  const { crumbs, primaryAction, searchItems } = usePageChromeState();
  const { projects } = useProjects();
  const [input, setInput] = React.useState("");
  const inputRef = React.useRef<HTMLInputElement>(null);

  const options: SearchItem[] = [
    ...projects.map((p) => ({
      key: `p-${p.id}`,
      label: p.name,
      secondary: p.description,
      group: "Projects" as const,
      to: `/dashboard/${p.id}`,
    })),
    ...searchItems,
  ];

  // ⌘K / Ctrl+K focuses the search from anywhere in the shell.
  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const action = primaryAction ?? {
    label: "Upload build",
    icon: "upload" as const,
    onClick: () => openUpload(),
  };

  return (
    <AppBar position="sticky" color="inherit" elevation={0}>
      <Toolbar sx={{ gap: 1.5, minHeight: { xs: 64, sm: 64 }, px: { xs: 2, md: 4 } }}>
        {showMenuButton && (
          <IconButton edge="start" onClick={onMenuClick} aria-label="Open menu">
            <MenuIcon />
          </IconButton>
        )}

        {crumbs.length > 0 && (
          <Breadcrumbs aria-label="breadcrumb">
            {crumbs.map((crumb, i) =>
              crumb.to && i < crumbs.length - 1 ? (
                <Link
                  key={`${i}-${crumb.label}`}
                  component="button"
                  underline="hover"
                  color="text.secondary"
                  variant="body2"
                  onClick={() => startTransition(() => navigate(crumb.to as string))}
                >
                  {crumb.label}
                </Link>
              ) : (
                <Typography
                  key={`${i}-${crumb.label}`}
                  component={i === crumbs.length - 1 ? "h1" : "span"}
                  sx={{
                    // The current page title is the bar's heading (19px / semibold).
                    fontSize: i === crumbs.length - 1 ? 19 : 14,
                    fontWeight: i === crumbs.length - 1 ? 600 : 400,
                    color: i === crumbs.length - 1 ? "text.primary" : "text.secondary",
                  }}
                >
                  {crumb.label}
                </Typography>
              )
            )}
          </Breadcrumbs>
        )}

        {process.env.REACT_APP_ENV_NAME && (
          <Chip size="small" color="warning" variant="outlined" label={process.env.REACT_APP_ENV_NAME} />
        )}

        <Box sx={{ flex: 1 }} />

        <Autocomplete<SearchItem, false, false, false>
          size="small"
          options={options}
          groupBy={(o) => o.group}
          getOptionLabel={(o) => o.label}
          getOptionKey={(o) => o.key}
          filterOptions={filterSearchOptions}
          value={null}
          inputValue={input}
          onInputChange={(_, v) => setInput(v)}
          open={input.trim().length > 0}
          autoHighlight
          blurOnSelect
          noOptionsText="No results"
          onChange={(_, o) => {
            if (o) {
              setInput("");
              startTransition(() => navigate(o.to));
            }
          }}
          renderOption={(props, o) => {
            const { key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key: string };
            return (
              <li key={key} {...rest}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" noWrap>
                    {o.label}
                  </Typography>
                  {o.secondary && (
                    <Typography variant="caption" color="textSecondary" noWrap sx={{ display: "block" }}>
                      {o.secondary}
                    </Typography>
                  )}
                </Box>
              </li>
            );
          }}
          sx={{ width: { xs: 160, md: 340 } }}
          renderInput={(params) => (
            <TextField
              {...params}
              inputRef={inputRef}
              sx={{ "& .MuiOutlinedInput-root": { height: 40, py: 0 } }}
              placeholder="Search projects, variants, versions…"
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <Box
                      component="span"
                      sx={{
                        display: { xs: "none", md: "inline-block" },
                        px: 0.75,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 0.5,
                        fontSize: 11,
                        color: "text.secondary",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {isMac ? "⌘K" : "Ctrl K"}
                    </Box>
                  ),
                },
              }}
            />
          )}
        />

        <Button
          variant="contained"
          startIcon={action.icon === "add" ? <AddIcon /> : <UploadIcon />}
          onClick={action.onClick}
        >
          {action.label}
        </Button>
      </Toolbar>
    </AppBar>
  );
};

export default TopBar;
